/**
 * Data Browser (GH #1824) -- the sanctioned AI/dev debug surface.
 *
 * platform.aaim.com / platformAdmin / AAIM_SUPER_ADMIN. Three modes:
 *  - Browse: pick a database + table, page through rows (sensitive columns hidden).
 *  - SQL: run a single read-only SELECT/WITH against a database.
 *  - Debug token: mint a short-TTL, IP-scoped, read-only bearer to paste into AI
 *    chat so the assistant queries the same guarded API; revoke when done.
 *
 * Every request is IP-allowlisted + audited server-side. Composes
 * @aaimplatform/ui primitives + semantic design tokens.
 */

import { useMemo, useState } from 'react';
import { DatabaseIcon, PlayIcon, KeyRoundIcon, CopyIcon, ShieldAlertIcon } from 'lucide-react';
import { Card, Button, Select, Textarea, Alert, Badge, Heading, Skeleton, DataGrid } from '@aaimplatform/ui';
import type { ColumnDef, PaginationState } from '@aaimplatform/ui';
import { useAuthStore } from '../stores/auth';
import { hasAnyRole, PLATFORM_ADMIN_EQUIVALENT_ROLES } from '../utils/role-helpers';
import {
  useDatabases,
  useTables,
  useRows,
  useRunQuery,
  useMintToken,
  useRevokeToken,
  type DatabaseKey,
  type TokenMintResponse,
  type DataRow,
  type DataBrowserViewTab,
} from '../hooks/useDataBrowser';

function renderCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function buildColumns(names: string[]): ColumnDef<DataRow>[] {
  return names.map((name) => ({
    accessorKey: name,
    header: name,
    cell: (info: { getValue: () => unknown }) => (
      <span className="font-mono text-xs text-secondary">{renderCell(info.getValue())}</span>
    ),
  }));
}

// UI placeholder copy for the operator SQL console, not a query call site (the
// gated raw-SQL surface lives in data-browser.routes.ts with its own markers).
const SQL_PLACEHOLDER = 'SELECT * FROM public.institutions LIMIT 50'; // compliance-skip: data-browser-gated-readonly-sql (placeholder copy, not a call site)

// Browse page sizes. admin-data-browser.openapi.yaml LimitParam: default 100,
// maximum 1000. Every option stays inside that range.
const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500, 1000];

export function DataBrowserPage() {
  const { user } = useAuthStore();
  const authorized = hasAnyRole(user?.roles, PLATFORM_ADMIN_EQUIVALENT_ROLES);

  const [tab, setTab] = useState<DataBrowserViewTab>('browse');
  const [db, setDb] = useState<DatabaseKey>('institutional');
  const [tableKey, setTableKey] = useState<string>('');
  // 1-based, as the rows endpoint takes it (PageParam minimum 1). The grid
  // works in a 0-based index; the pagination state below converts.
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);

  const databasesQ = useDatabases();
  const tablesQ = useTables(db);
  const [schema, table] = tableKey ? (tableKey.split('.') as [string, string]) : [undefined, undefined];
  const rowsQ = useRows({ db, schema, table, page, limit });

  const [sql, setSql] = useState('SELECT 1');
  const runQuery = useRunQuery();

  const mint = useMintToken();
  const revoke = useRevokeToken();
  const [minted, setMinted] = useState<TokenMintResponse | null>(null);
  const [revoked, setRevoked] = useState(false);

  const dbOptions = useMemo(
    () => (databasesQ.data?.data ?? []).map((d) => ({ value: d.key, label: d.label })),
    [databasesQ.data],
  );
  const tableOptions = useMemo(
    () =>
      (tablesQ.data?.data ?? []).map((t) => ({
        value: `${t.schema}.${t.name}`,
        label: `${t.schema}.${t.name}${t.kind === 'view' ? '  (view)' : ''}`,
      })),
    [tablesQ.data],
  );

  const rowColumns = useMemo(() => buildColumns(rowsQ.data?.columns ?? []), [rowsQ.data]);
  const queryColumns = useMemo(() => buildColumns(runQuery.data?.columns ?? []), [runQuery.data]);

  if (!authorized) {
    return (
      <div className="p-6">
        <Alert type="error" title="Not authorized">
          The Data Browser is restricted to Aaim platform super-admins.
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <DatabaseIcon className="h-6 w-6 text-accent" aria-hidden />
        <div>
          {/* Level 2: PlatformAdminLayout's AppHeader renders the page h1
              (operator ruling on GH #2664, 2026-08-20). */}
          <Heading level={2} size="page">Data Browser</Heading>
          <p className="text-sm text-tertiary">
            Operator-scoped, IP-allowlisted, audited read-only access to the institutional and
            consumer databases. Sensitive columns are hidden; every query is logged.
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-default">
        {([
          { id: 'browse', label: 'Browse Tables' },
          { id: 'sql', label: 'Run SQL' },
          { id: 'token', label: 'Debug Token' },
        ] as { id: DataBrowserViewTab; label: string }[]).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id ? 'border-accent text-accent' : 'border-transparent text-tertiary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Database picker (shared) */}
      <div className="max-w-xs">
        <Select
          label="Database"
          value={db}
          options={dbOptions}
          onChange={(e) => {
            setDb(e.target.value as DatabaseKey);
            setTableKey('');
            setPage(1);
          }}
        />
      </div>

      {tab === 'browse' && (
        <Card title="Browse">
          <div className="space-y-4">
            <div className="max-w-md">
              <Select
                label="Table"
                value={tableKey}
                options={tableOptions}
                placeholder={tablesQ.isLoading ? 'Loading tables...' : 'Select a table'}
                onChange={(e) => {
                  setTableKey(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            {rowsQ.isError && (
              <Alert type="error" title="Failed to load rows">
                {(rowsQ.error as Error)?.message ?? 'Unknown error'}
              </Alert>
            )}

            {tableKey && rowsQ.isLoading && (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}

            {tableKey && rowsQ.data && (
              <BrowseGrid
                rows={rowsQ.data.data as DataRow[]}
                columns={rowColumns}
                total={rowsQ.data.total ?? null}
                page={page}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(size) => {
                  setLimit(size);
                  setPage(1);
                }}
              />
            )}
          </div>
        </Card>
      )}

      {tab === 'sql' && (
        <Card title="Run read-only SQL">
          <div className="space-y-4">
            <Textarea
              label="SQL (single read-only SELECT or WITH statement)"
              value={sql}
              rows={6}
              onChange={(v) => setSql(v)}
              placeholder={SQL_PLACEHOLDER}
            />
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                icon={PlayIcon}
                disabled={runQuery.isPending || sql.trim().length === 0}
                onClick={() => runQuery.mutate({ db, sql })}
              >
                {runQuery.isPending ? 'Running...' : 'Run'}
              </Button>
              {runQuery.data?.truncated && (
                <Badge variant="warning">Truncated at row cap (1000)</Badge>
              )}
            </div>

            {runQuery.isError && (
              <Alert type="error" title="Query rejected">
                {(runQuery.error as Error)?.message ?? 'Unknown error'}
              </Alert>
            )}

            {runQuery.data && (
              <>
                <p className="text-sm text-tertiary">{runQuery.data.rowCount} rows</p>
                <DataGrid<DataRow> data={runQuery.data.data as DataRow[]} columns={queryColumns} />
              </>
            )}
          </div>
        </Card>
      )}

      {tab === 'token' && (
        <Card title="Ephemeral debug token">
          <div className="space-y-4">
            <Alert type="warning" title="Paste into AI chat only">
              This mints a short-TTL (up to 30 min), IP-scoped, read-only bearer scoped to
              <span className="font-mono"> data-browser:read</span>. It is NOT your session token.
              Paste it into the assistant so it can query this same guarded API. Revoke it when done.
            </Alert>

            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                icon={KeyRoundIcon}
                disabled={mint.isPending}
                onClick={() => {
                  setRevoked(false);
                  mint.mutate(
                    { ttlMinutes: 15 },
                    { onSuccess: (data) => setMinted(data) },
                  );
                }}
              >
                {mint.isPending ? 'Minting...' : 'Mint debug token'}
              </Button>
            </div>

            {mint.isError && (
              <Alert type="error" title="Mint failed">
                {(mint.error as Error)?.message ?? 'Unknown error'}
              </Alert>
            )}

            {minted && !revoked && (
              <div className="space-y-3 rounded-sm border border-default p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-secondary">Token (shown once)</span>
                  <Button
                    variant="ghost"
                    icon={CopyIcon}
                    onClick={() => void navigator.clipboard.writeText(minted.token)}
                  >
                    Copy
                  </Button>
                </div>
                <code className="block w-full break-all rounded-sm bg-recessed p-3 font-mono text-xs text-secondary">
                  {minted.token}
                </code>
                <div className="flex flex-wrap gap-4 text-xs text-tertiary">
                  <span>jti: <span className="font-mono">{minted.jti}</span></span>
                  <span>scope: <span className="font-mono">{minted.scope}</span></span>
                  <span>expires: {new Date(minted.expiresAt).toLocaleString()}</span>
                </div>
                <Button
                  variant="secondary"
                  icon={ShieldAlertIcon}
                  disabled={revoke.isPending}
                  onClick={() =>
                    revoke.mutate({ jti: minted.jti }, { onSuccess: () => setRevoked(true) })
                  }
                >
                  {revoke.isPending ? 'Revoking...' : 'Revoke now'}
                </Button>
              </div>
            )}

            {revoked && (
              <Alert type="success" title="Revoked">
                The token has been blocklisted and can no longer be used.
              </Alert>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

interface BrowseGridProps {
  rows: DataRow[];
  columns: ColumnDef<DataRow>[];
  /** RowList.total; null when the server could not count the table. */
  total: number | null;
  /** 1-based page, as sent to the rows endpoint. */
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

/**
 * The Browse grid pages on the server (GH #2927). With a total, the DataGrid's
 * own footer drives the rows endpoint. Without one, the grid shows the current
 * server page and a footer that says the total is unknown; Next stays enabled
 * while the server returns a full page.
 */
function BrowseGrid({ rows, columns, total, page, limit, onPageChange, onLimitChange }: BrowseGridProps) {
  if (total !== null) {
    const pagination: PaginationState = {
      page: page - 1,
      pageSize: limit,
      total,
      onPageChange: (index) => onPageChange(index + 1),
      onPageSizeChange: onLimitChange,
    };
    return (
      <DataGrid<DataRow>
        data={rows}
        columns={columns}
        pagination={pagination}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />
    );
  }

  // A table whose row count is an exact multiple of the page size leaves Next
  // enabled on its last full page, and the page after it is empty. That page
  // has no row range, so the footer says so instead of a reversed range.
  const start = (page - 1) * limit + 1;
  const end = (page - 1) * limit + rows.length;
  const range = rows.length === 0 ? 'No rows on this page' : `Rows ${start}–${end}`;
  return (
    <div>
      <DataGrid<DataRow> data={rows} columns={columns} enablePagination={false} />
      <div
        className="flex items-center justify-between gap-3 border-t border-recessed px-4 py-2 text-sm text-tertiary"
        data-testid="data-browser-unknown-total-footer"
      >
        <span className="tabular-nums">
          {range} &middot; total not available
        </span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            Prev
          </Button>
          <span className="tabular-nums">Page {page}</span>
          <Button variant="ghost" disabled={rows.length < limit} onClick={() => onPageChange(page + 1)}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DataBrowserPage;
